// src/store/slices/authSlice.ts
import { jwtDecode } from "jwt-decode";

import { authService } from "@/services/authService";
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

interface DecodedToken {
    exp: number;
    sub: string;
    email: string;
    username: string;
}

interface User {
    id: string;
    email: string;
    username: string;
    avatar?: string;
    role?: string;
}

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
}

const initialState: AuthState = {
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        initializeAuth: (state) => {
            const token = authService.getToken();
            if (token && authService.isTokenValid()) {
                try {
                    const decoded = jwtDecode<DecodedToken>(token);
                    state.isAuthenticated = true;
                    state.token = token;
                    state.user = {
                        id: decoded.sub,
                        email: decoded.email,
                        username: decoded.username
                    };
                } catch {
                    state.isAuthenticated = false;
                    state.token = null;
                    state.user = null;
                }
            } else {
                state.isAuthenticated = false;
                state.token = null;
                state.user = null;
            }
        },
        loginStart: (state) => {
            state.isLoading = true;
            state.error = null;
        },
        loginSuccess: (
            state,
            action: PayloadAction<{ user: User; token: string }>
        ) => {
            state.isLoading = false;
            state.isAuthenticated = true;
            state.user = action.payload.user;
            state.token = action.payload.token;
            state.error = null;
        },
        loginFailure: (state, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },
        logout: (state) => {
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
});

export const { loginStart, loginSuccess, loginFailure, logout, clearError, initializeAuth } =
    authSlice.actions;
export default authSlice.reducer;
